import { jsx as _jsx } from "react/jsx-runtime";
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from 'chart.js';
import { useTheme } from '../../contexts/ThemeContext';
// Register the annotation plugin for dashed status lines
const annotationPlugin = {
    id: 'annotation',
    beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea)
            return;
        // CRITICAL: Check if scales.y has getPixelForValue (only for linear scales)
        // Horizontal charts (indexAxis: 'y') have CategoryScale on y-axis
        if (!scales.y || typeof scales.y.getPixelForValue !== 'function') {
            return;
        }
        // Status lines with dashed pattern
        const statusLines = [
            { value: 100, color: '#22c55e', label: 'Excellent' }, // Strong green
            { value: 85, color: '#eab308', label: 'Warning' }, // Strong yellow
            { value: 60, color: '#ef4444', label: 'Critical' }, // Strong red
        ];
        statusLines.forEach(({ value, color }) => {
            const yPosition = scales.y.getPixelForValue(value);
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([5, 5]); // Dashed lines
            ctx.lineWidth = 2;
            ctx.strokeStyle = color;
            ctx.moveTo(chartArea.left, yPosition);
            ctx.lineTo(chartArea.right, yPosition);
            ctx.stroke();
            ctx.restore();
        });
    },
};
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);
const DeviceComparisonChart = ({ data, title = 'Device Comparison', metricType = 'all', height = 300, className = '', }) => {
    const { isDark } = useTheme();
    const getMetricColor = (metric) => {
        switch (metric) {
            case 'oee':
                return 'rgb(59, 130, 246)';
            case 'availability':
                return 'rgb(34, 197, 94)';
            case 'performance':
                return 'rgb(251, 146, 60)';
            case 'quality':
                return 'rgb(168, 85, 247)';
            default:
                return 'rgb(107, 114, 128)';
        }
    };
    const getMetricLabel = (metric) => {
        switch (metric) {
            case 'oee':
                return 'OEE';
            case 'availability':
                return 'Availability';
            case 'performance':
                return 'Performance';
            case 'quality':
                return 'Quality';
            default:
                return metric;
        }
    };
    const chartData = {
        labels: data.map((d) => d.deviceName),
        datasets: metricType === 'all'
            ? [
                {
                    label: 'OEE',
                    data: data.map((d) => d.oee),
                    backgroundColor: getMetricColor('oee'),
                },
                {
                    label: 'Availability',
                    data: data.map((d) => d.availability),
                    backgroundColor: getMetricColor('availability'),
                },
                {
                    label: 'Performance',
                    data: data.map((d) => d.performance),
                    backgroundColor: getMetricColor('performance'),
                },
                {
                    label: 'Quality',
                    data: data.map((d) => d.quality),
                    backgroundColor: getMetricColor('quality'),
                },
            ]
            : [
                {
                    label: getMetricLabel(metricType),
                    data: data.map((d) => d[metricType]),
                    backgroundColor: getMetricColor(metricType),
                },
            ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: metricType === 'all',
                position: 'top',
                labels: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
            },
            title: {
                display: true,
                text: title,
                font: {
                    size: 16,
                    weight: 'bold',
                },
                color: isDark ? 'rgb(243, 244, 246)' : 'rgb(17, 24, 39)',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        var _a;
                        return `${context.dataset.label}: ${(_a = context.parsed.y) === null || _a === void 0 ? void 0 : _a.toFixed(1)}%`;
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Devices',
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                ticks: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                grid: {
                    color: isDark ? 'rgb(55, 65, 81)' : 'rgb(229, 231, 235)',
                },
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: 'Percentage (%)',
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                min: 0,
                max: 100,
                ticks: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                    callback: function (value) {
                        return value + '%';
                    },
                },
                grid: {
                    display: false, // Remove horizontal grid lines
                },
            },
        },
    };
    return (_jsx("div", { className: `bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`, children: _jsx("div", { style: { height }, children: _jsx(Bar, { data: chartData, options: options }) }) }));
};
export default DeviceComparisonChart;
